import {
  extractFontNames,
  extractHexColors,
  formatHandoffBriefGroupPlainText,
  listHandoffSnapshotFields,
  looksLikeHandoffUrl,
  parseHandoffLinkHost,
  resolveThumbnailGuidelines,
  type HandoffBriefCopyGroup,
  type HandoffSnapshotField,
  type HandoffSnapshotFieldKey,
  type HandoffSnapshotFieldView,
  type MarketplaceOrderHandoffDefaults,
  type MarketplaceOrderHandoffSnapshot,
} from "@nexus/contracts";
import {
  Button,
  Icon,
  SegmentedControl,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useId, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useAuth } from "@/features/auth/hooks/useAuth";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { orderHandoffDefaultsQueryOptions } from "@/lib/queries";

import {
  briefDiffPair,
  classifyBriefValue,
  filterBriefFields,
  orgDefaultValueForField,
  partitionBriefFields,
  withoutPinnedBriefFields,
  readVendorBriefExpanded,
  readVendorBriefGroups,
  shouldShowOverrideRails,
  shouldShowVendorBriefSection,
  summarizeVendorBrief,
  vendorBriefIsRecorded,
  VENDOR_BRIEF_INSTRUCTIONS_CLAMP,
  VENDOR_BRIEF_LABEL_COL_PX,
  VENDOR_BRIEF_LONG_CLAMP,
  VENDOR_BRIEF_RAIL_PX,
  VENDOR_BRIEF_ROW_MIN_PX,
  writeVendorBriefExpanded,
  writeVendorBriefGroups,
  type BriefCollapsibleGroup,
  type BriefFilter,
  type BriefGroupState,
} from "../../lib/vendor-brief-display";
import type { MarketplaceFulfillmentViewer } from "../../lib/fulfillment-badge-variant";
import { FIELD_LABELS } from "./vendor-brief/vendor-brief-labels";

function faviconUrl(host: string): string {
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=32`;
}

function BriefIconAction({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

function BriefMetaFacts({
  fieldCount,
  editedCount,
  allEdited,
  sentLabel,
}: {
  fieldCount: number;
  editedCount: number;
  allEdited: boolean;
  sentLabel: string | null;
}) {
  const { t } = useTranslation();
  const facts = [
    t("marketplace.orders.list.brief.metaFields", {
      count: fieldCount,
      defaultValue: "{{count}} fields",
    }),
    allEdited
      ? t("marketplace.orders.list.brief.metaAllEdited", "all edited for this order")
      : editedCount > 0
        ? t("marketplace.orders.list.brief.metaEdited", {
            count: editedCount,
            defaultValue: "{{count}} edited for this order",
          })
        : null,
    sentLabel
      ? t("marketplace.orders.list.brief.metaSent", {
          date: sentLabel,
          defaultValue: "sent {{date}}",
        })
      : null,
  ].filter((fact): fact is string => Boolean(fact));

  return (
    <p className="text-[12px] leading-5 text-muted-foreground">
      {facts.map((fact, index) => (
        <span key={fact}>
          {index > 0 ? <span aria-hidden> · </span> : null}
          {fact}
        </span>
      ))}
    </p>
  );
}

function HandoffLinkChip({ href }: { href: string }) {
  const host = parseHandoffLinkHost(href) ?? href;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-sm border border-border bg-muted/40 px-1.5 py-0.5 text-primary hover:bg-muted"
    >
      <img
        src={faviconUrl(host)}
        alt=""
        width={14}
        height={14}
        className="size-3.5 shrink-0 rounded-sm"
      />
      <span className="truncate font-mono text-[12px]">{host}</span>
    </a>
  );
}

function HexValue({ value }: { value: string }) {
  const colors = extractHexColors(value);
  if (colors.length === 0) return <span>{value}</span>;
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {colors.map((color) => (
        <span
          key={color}
          className="inline-flex items-center gap-1 rounded-sm border border-border bg-muted/30 px-1 py-0.5"
        >
          <span
            aria-hidden
            className="size-3.5 rounded-sm border border-border"
            style={{ backgroundColor: color }}
          />
          <span className="font-mono text-[11px] text-foreground">{color}</span>
        </span>
      ))}
    </span>
  );
}

function FontValue({ value }: { value: string }) {
  const fonts = extractFontNames(value);
  if (fonts.length === 0) return <span>{value}</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {fonts.map((font) => (
        <span
          key={font}
          className="rounded-sm bg-muted px-1.5 py-0.5 text-[12px] font-medium text-foreground"
        >
          {font}
        </span>
      ))}
    </span>
  );
}

const BRIEF_LINK_PATTERN =
  /(https?:\/\/[^\s]+|(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/[^\s]*)?)/gi;
const BRIEF_LINK_EXACT_PATTERN =
  /^(https?:\/\/[^\s]+|(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/[^\s]*)?)$/i;

function LinkifiedText({ value }: { value: string }) {
  const parts = value.split(BRIEF_LINK_PATTERN);
  return (
    <>
      {parts.map((part, index) => {
        if (!part) return part;
        const match = part.match(/^(.+?)([.,;:!?)]*)$/);
        const linkText = match?.[1] ?? part;
        const suffix = match?.[2] ?? "";
        if (!BRIEF_LINK_EXACT_PATTERN.test(linkText)) return part;
        const href = linkText.match(/^https?:\/\//i)
          ? linkText
          : `https://${linkText}`;
        try {
          const parsed = new URL(href);
          if (!["http:", "https:"].includes(parsed.protocol)) return part;
          return (
            <span key={`${part}-${index}`}>
              <a
                href={parsed.toString()}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2"
              >
                {linkText}
              </a>
              {suffix}
            </span>
          );
        } catch {
          return part;
        }
      })}
    </>
  );
}

function ClampedText({
  value,
  lines,
}: {
  value: string;
  lines: number;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const isLong =
    value.split("\n").length > lines || value.length > 80;
  return (
    <div className="min-w-0">
      <p
        className={cn(
          "whitespace-pre-wrap text-foreground",
          !expanded && isLong && (lines === 3 ? "line-clamp-3" : "line-clamp-2"),
        )}
      >
        <LinkifiedText value={value} />
      </p>
      {isLong ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-fit px-0 text-xs text-muted-foreground"
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded
            ? t("marketplace.orders.list.panel.showLess", "Show less")
            : t("marketplace.orders.list.panel.showMore", "Show more")}
        </Button>
      ) : null}
    </div>
  );
}

function TypedBriefValue({
  fieldKey,
  field,
}: {
  fieldKey: HandoffSnapshotFieldKey;
  field: HandoffSnapshotField;
}) {
  const kind = classifyBriefValue(fieldKey, field.value, field.folderLink);
  const href =
    field.folderLink ?? (looksLikeHandoffUrl(field.value) ? field.value.trim() : null);
  if (kind === "url" && href) return <HandoffLinkChip href={href} />;
  if (kind === "hex") return <HexValue value={field.value} />;
  if (kind === "font") return <FontValue value={field.value} />;
  if (kind === "long") {
    return <ClampedText value={field.value} lines={VENDOR_BRIEF_LONG_CLAMP} />;
  }
  return (
    <span className="text-foreground">
      <LinkifiedText value={field.value} />
    </span>
  );
}

function CopyRowButton({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  const { t } = useTranslation();
  const { copy } = useCopyToClipboard({
    onCopy: () =>
      toast.success(t("marketplace.orders.list.brief.copied", "Copied")),
  });
  return (
    <BriefIconAction label={label}>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className="size-7 text-muted-foreground opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 motion-reduce:transition-none"
        aria-label={label}
        onClick={() => void copy(value)}
      >
        <Icon name="copy" size="xs" color="inherit" />
      </Button>
    </BriefIconAction>
  );
}

function ThumbnailRequest({ snapshot }: { snapshot: MarketplaceOrderHandoffSnapshot }) {
  const { t } = useTranslation();
  const field = snapshot.thumbnailGuidelines;
  if (!field) return null;
  const guidelines = resolveThumbnailGuidelines(field.value);
  const href = looksLikeHandoffUrl(guidelines) ? guidelines.trim() : null;
  const ratios = snapshot.targetRatios ?? [];
  const lines =
    ratios.length === 0
      ? [t("marketplace.orders.list.brief.thumbnailDefault", "One default thumbnail")]
      : ratios.map((ratio) =>
          t("marketplace.orders.list.brief.thumbnailAtRatio", {
            ratio,
            defaultValue: "One thumbnail at {{ratio}}",
          }),
        );

  return (
    <div className="flex max-w-[70ch] flex-col gap-1" data-brief-thumbnails>
      <p className="text-xs font-medium text-foreground">
        {t("marketplace.orders.list.brief.thumbnailsTitle", "Thumbnails")}
      </p>
      <p className="text-[12px] leading-5 text-muted-foreground">
        {t("marketplace.orders.list.brief.thumbnailsExpected", "Optional but expected.")}
      </p>
      <ul className="list-disc pl-4 text-[13px] leading-relaxed text-foreground">
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {href ? (
        <HandoffLinkChip href={href} />
      ) : (
        <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-foreground">{guidelines}</p>
      )}
    </div>
  );
}

function KvRow({
  row,
  orgDefault,
  showRail,
}: {
  row: HandoffSnapshotFieldView;
  orgDefault: string;
  showRail: boolean;
}) {
  const { t } = useTranslation();
  const label = FIELD_LABELS[row.key];
  const isOverride = row.field.source === "order_override";
  const diff = briefDiffPair(row.field.value, orgDefault);

  return (
    <div
      data-brief-row={row.key}
      data-brief-override={isOverride ? "true" : "false"}
      className="group grid items-start border-b border-border-subtle last:border-b-0"
      style={{
        gridTemplateColumns: `${VENDOR_BRIEF_RAIL_PX}px ${VENDOR_BRIEF_LABEL_COL_PX}px minmax(0,1fr) auto`,
        minHeight: VENDOR_BRIEF_ROW_MIN_PX,
      }}
    >
      <span
        aria-hidden
        className={cn(
          "self-stretch",
          showRail && isOverride ? "bg-primary" : "bg-transparent",
        )}
      />
      <dt className="px-2 py-1.5 text-[12px] text-muted-foreground">
        {t(label.key, label.fallback)}
      </dt>
      <dd className="min-w-0 px-1 py-1.5 text-[13px]">
        {isOverride && diff.differs ? (
          <p className="mb-0.5 hidden text-[11px] text-muted-foreground group-hover:block group-focus-within:block">
            <span className="mr-1">
              {t("marketplace.orders.list.brief.orgDefault", "Org default")}:
            </span>
            <span className="line-through">{diff.orgDefault}</span>
          </p>
        ) : null}
        <TypedBriefValue fieldKey={row.key} field={row.field} />
      </dd>
      <div className="flex items-start py-0.5 pr-0.5">
        <CopyRowButton
          label={t("marketplace.orders.list.brief.copyField", "Copy field")}
          value={row.field.value}
        />
      </div>
    </div>
  );
}

function ProseField({
  row,
}: {
  row: HandoffSnapshotFieldView;
}) {
  const { t } = useTranslation();
  const label = FIELD_LABELS[row.key];
  return (
    <dl data-brief-row={row.key} className="group flex max-w-[70ch] flex-col">
      <div className="flex items-center gap-2">
        <dt className="text-[12px] text-muted-foreground">
          {t(label.key, label.fallback)}
        </dt>
        <CopyRowButton
          label={t("marketplace.orders.list.brief.copyField", "Copy field")}
          value={row.field.value}
        />
      </div>
      <dd className="mt-1 text-[15px] leading-relaxed text-foreground">
        <ClampedText
          value={row.field.value}
          lines={
            row.key === "orderInstructions"
              ? VENDOR_BRIEF_INSTRUCTIONS_CLAMP
              : VENDOR_BRIEF_LONG_CLAMP
          }
        />
      </dd>
    </dl>
  );
}

function GroupHeader({
  title,
  count,
  expanded,
  onToggle,
  onCopy,
  copyLabel,
}: {
  title: string;
  count: number;
  expanded: boolean;
  onToggle: () => void;
  onCopy: () => void;
  copyLabel: string;
}) {
  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        aria-expanded={expanded}
        className="flex min-h-8 min-w-0 flex-1 cursor-pointer items-center gap-1.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
        onClick={onToggle}
      >
        <span className="text-[13px] font-medium text-foreground">
          {title}
          <span className="font-normal text-muted-foreground"> · {count}</span>
        </span>
        <Icon
          name="chevron-down"
          size="xs"
          color="muted"
          className={cn(
            "shrink-0 transition-transform duration-150 ease-out motion-reduce:transition-none",
            expanded && "rotate-180",
          )}
        />
      </button>
      <BriefIconAction label={copyLabel}>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="size-7 text-muted-foreground"
          aria-label={copyLabel}
          data-brief-copy-group
          onClick={onCopy}
        >
          <Icon name="copy" size="xs" color="inherit" />
        </Button>
      </BriefIconAction>
    </div>
  );
}

interface VendorBriefSectionProps {
  snapshot: MarketplaceOrderHandoffSnapshot | null | undefined;
  tenantId: string;
  tenantVendorId?: string | null;
  orgDefaults?: MarketplaceOrderHandoffDefaults | null;
  userId?: string | null;
  viewer?: MarketplaceFulfillmentViewer;
  scripts?: ReadonlyArray<{ customAssetKind?: "SCRIPT" | "BRIEF" | null }>;
}

export function VendorBriefSection({
  snapshot,
  tenantId,
  tenantVendorId,
  orgDefaults,
  userId,
  viewer = "client",
  scripts,
}: VendorBriefSectionProps) {
  const { t, i18n } = useTranslation();
  const bodyId = useId();
  const authUserId = useAuth((state) => state.user?.sub);
  const persistenceUserId = userId ?? authUserId ?? "anon";
  const { copy } = useCopyToClipboard({
    onCopy: () =>
      toast.success(t("marketplace.orders.list.brief.copied", "Copied")),
  });
  const [open, setOpen] = useState(() => readVendorBriefExpanded(persistenceUserId));
  const [groups, setGroups] = useState<BriefGroupState>(() =>
    readVendorBriefGroups(persistenceUserId),
  );
  const [filter, setFilter] = useState<BriefFilter>("all");

  const showSection = shouldShowVendorBriefSection(snapshot, scripts);
  const defaultsQuery = useQuery({
    ...orderHandoffDefaultsQueryOptions(tenantId, tenantVendorId),
    enabled:
      showSection &&
      orgDefaults === undefined &&
      Boolean(tenantId && tenantVendorId),
  });
  const resolvedDefaults = orgDefaults ?? defaultsQuery.data?.defaults ?? null;

  const recorded = vendorBriefIsRecorded(snapshot);
  if (!showSection) return null;
  const allFields = snapshot ? listHandoffSnapshotFields(snapshot) : [];
  const summary = summarizeVendorBrief(allFields);
  const showRails = shouldShowOverrideRails(summary);
  const visibleFields = withoutPinnedBriefFields(
    filterBriefFields(allFields, filter),
  );
  const partitioned = partitionBriefFields(visibleFields);
  const sentLabel = snapshot?.sentAt
    ? new Intl.DateTimeFormat(i18n.language, {
        month: "short",
        day: "numeric",
      }).format(new Date(snapshot.sentAt))
    : null;
  const copyGroupLabel = t(
    "marketplace.orders.list.brief.copyGroup",
    "Copy group",
  );
  const instructions = allFields.find((row) => row.key === "orderInstructions");

  const setExpanded = (next: boolean) => {
    setOpen(next);
    writeVendorBriefExpanded(persistenceUserId, next);
  };

  const setGroupOpen = (group: BriefCollapsibleGroup, next: boolean) => {
    setGroups((current) => {
      const updated = { ...current, [group]: next };
      writeVendorBriefGroups(persistenceUserId, updated);
      return updated;
    });
  };

  const copyGroup = (group: HandoffBriefCopyGroup) => {
    if (!snapshot) return;
    void copy(formatHandoffBriefGroupPlainText(snapshot, group));
  };

  const title = t(
    "marketplace.orders.list.brief.handoffTitle",
    "Partner Brand Guidelines",
  );
  const viewLabel = t(
    "marketplace.orders.list.brief.viewHandoff",
    "View Brand Guidelines",
  );
  const hideLabel = t(
    "marketplace.orders.list.brief.hideHandoff",
    "Hide Brand Guidelines",
  );
  return (
    <section
      data-orders-section="vendorBrief"
      data-brief-state={recorded ? (open ? "expanded" : "collapsed") : "legacy"}
      className="flex flex-col gap-3"
    >
      <header className="flex flex-col gap-1">
        <div className="flex min-h-8 items-center gap-2">
          <h3 className="min-w-0 flex-1 truncate text-drawer-body font-medium tracking-[-0.01em] text-foreground">
            {title}
          </h3>
          {recorded ? (
            <Button
              type="button"
              size="sm"
              className="shrink-0"
              aria-expanded={open}
              aria-controls={bodyId}
              onClick={() => setExpanded(!open)}
            >
              {open ? hideLabel : viewLabel}
            </Button>
          ) : null}
        </div>
        {recorded ? (
          <BriefMetaFacts
            fieldCount={summary.fieldCount}
            editedCount={summary.editedCount}
            allEdited={summary.allEdited}
            sentLabel={sentLabel}
          />
        ) : null}
      </header>

      {!recorded ? (
        <p className="text-drawer-body text-muted-foreground">
          {viewer === "partner" ? (
            t(
              "marketplace.orders.list.brief.vendorEmpty",
              "No brief was provided for this order.",
            )
          ) : (
            <>
              {t(
                "marketplace.orders.list.brief.empty",
                "No brief recorded for this order.",
              )}{" "}
              <Link to="/iam" className="text-primary hover:underline">
                {t(
                  "marketplace.orders.list.brief.orgSettings",
                  "Open organisation settings",
                )}
              </Link>
            </>
          )}
        </p>
      ) : (
        <>
          {instructions ? (
            <div className="max-w-[70ch] text-[15px] leading-relaxed">
              <ClampedText
                value={instructions.field.value}
                lines={VENDOR_BRIEF_INSTRUCTIONS_CLAMP}
              />
            </div>
          ) : null}

          {open ? (
            <div id={bodyId} className="flex flex-col gap-4">
              {snapshot?.thumbnailGuidelines ? <ThumbnailRequest snapshot={snapshot} /> : null}
              {summary.listEditedCount > 0 ? (
                <SegmentedControl
                  value={filter}
                  onValueChange={(next) => setFilter(next as BriefFilter)}
                  ariaLabel={t(
                    "marketplace.orders.list.brief.filterAria",
                    "Filter brief fields",
                  )}
                  tone="subtle"
                  accent="surface"
                  options={[
                    {
                      value: "all",
                      label: t("marketplace.orders.list.brief.filterAll", "All"),
                    },
                    {
                      value: "edited",
                      label: t("marketplace.orders.list.brief.filterEdited", {
                        count: summary.listEditedCount,
                        defaultValue: "Edited ({{count}})",
                      }),
                    },
                  ]}
                />
              ) : null}

              {visibleFields.length === 0 ? (
                <p className="text-drawer-body text-muted-foreground">
                  {t(
                    "marketplace.orders.list.brief.noOverrides",
                    "No fields were edited for this order.",
                  )}
                </p>
              ) : (
                <>
                  {partitioned.perOrder.map((row) => (
                      <ProseField key={row.key} row={row} />
                    ))}

                  {partitioned.rules.length > 0 ? (
                    <div className="flex flex-col gap-1">
                      <GroupHeader
                        title={t("marketplace.orders.list.brief.sectionRules", "Rules")}
                        count={partitioned.rules.length}
                        expanded={groups.rules}
                        onToggle={() => setGroupOpen("rules", !groups.rules)}
                        onCopy={() => copyGroup("rules")}
                        copyLabel={copyGroupLabel}
                      />
                      {groups.rules ? (
                        <dl>
                          {partitioned.rules.map((row) => (
                            <KvRow
                              key={row.key}
                              row={row}
                              orgDefault={orgDefaultValueForField(
                                row.key,
                                resolvedDefaults,
                              )}
                              showRail={showRails}
                            />
                          ))}
                        </dl>
                      ) : null}
                    </div>
                  ) : null}

                  {partitioned.brandKit.length > 0 ? (
                    <div className="flex flex-col gap-1">
                      <GroupHeader
                        title={t(
                          "marketplace.orders.list.brief.sectionBrandKit",
                          "Brand kit",
                        )}
                        count={partitioned.brandKit.length}
                        expanded={groups.brandKit}
                        onToggle={() => setGroupOpen("brandKit", !groups.brandKit)}
                        onCopy={() => copyGroup("brandKit")}
                        copyLabel={copyGroupLabel}
                      />
                      {groups.brandKit ? (
                        <dl>
                          {partitioned.brandKit.map((row) => (
                            <KvRow
                              key={row.key}
                              row={row}
                              orgDefault={orgDefaultValueForField(
                                row.key,
                                resolvedDefaults,
                              )}
                              showRail={showRails}
                            />
                          ))}
                        </dl>
                      ) : null}
                    </div>
                  ) : null}
                </>
              )}
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
